import React from "react";
import { message, Modal } from "antd";

import { ApiManager, Locale, Logger, Moment, Util } from "../utils";
import { JD_ID, GROUPON_ID, SUPERMARKET_ID } from "../constants";

class Base extends React.Component {
  /* lifecycle methods */

  static getInstance() {
    if (!Base.base) Base.base = new Base();
    return Base.base;
  }

  constructor(props) {
    super(props);

    this.logger = Logger.getInstance();
    this.i18n = Locale.getInstance();
    this.api = ApiManager.getInstance();
    this.moment = Moment.getInstance();
    this.util = Util.getInstance();

    this.state = {
      previewImages: null,
      previewImageIndex: 0,
      isImg: true,
    };
  }

  componentDidMount() {
    this.mounted = true;

    message.config({
      top: 56,
      duration: 2,
    });
  }

  componentDidUpdate(prevProps, prevState) {}

  componentWillUnmount() {
    this.mounted = false;
  }

  /* message methods */

  static Message = {
    success: "sucess",
    error: "error",
    info: "info",
    warning: "warning",
    loading: "loading",
  };

  showMessage(content = "", type = Base.Message.info) {
    switch (type) {
      case Base.Message.success: {
        message.success(content);
        break;
      }
      case Base.Message.error: {
        message.error(content);
        break;
      }
      case Base.Message.info: {
        message.info(content);
        break;
      }
      case Base.Message.warning: {
        message.warning(content);
        break;
      }
      case Base.Message.loading: {
        message.loading(content);
        break;
      }
      default: {
        message.info(content);
        break;
      }
    }
  }

  /* modal methods */

  static Modal = {
    success: "sucess",
    error: "error",
    info: "info",
    warning: "warning",
    confirm: "confirm",
  };

  showModal(title, type = Base.Modal.info, onOk, custom = {}) {
    this.hideModal();

    switch (type) {
      case Base.Modal.success: {
        let config = {
          title: title || this.i18n.t("base.modal.successTitle"),
          okText: this.i18n.t("base.modal.knowText"),
          onOk: onOk,
        };

        this.modal = Modal.success({ ...config, ...custom });

        break;
      }
      case Base.Modal.error: {
        let config = {
          title: title || this.i18n.t("base.modal.errorTitle"),
          okText: this.i18n.t("base.modal.knowText"),
          onOk: onOk,
        };

        this.modal = Modal.error({ ...config, ...custom });

        break;
      }
      case Base.Modal.warning: {
        let config = {
          title: title || this.i18n.t("base.modal.warningTitle"),
          okText: this.i18n.t("base.modal.knowText"),
          onOk: onOk,
        };

        this.modal = Modal.warning({ ...config, ...custom });

        break;
      }
      case Base.Modal.confirm: {
        let config = {
          title: title || this.i18n.t("base.modal.confirmTitle"),
          okText: this.i18n.t("base.modal.okText"),
          cancelText: this.i18n.t("base.modal.cancelText"),
          onOk: onOk,
        };

        this.modal = Modal.confirm({ ...config, ...custom });

        break;
      }
      case Base.Modal.info:
      default: {
        let config = {
          title: title || this.i18n.t("base.modal.infoTitle"),
          okText: this.i18n.t("base.modal.knowText"),
          onOk: onOk,
        };

        this.modal = Modal.info({ ...config, ...custom });

        break;
      }
    }
  }

  hideModal() {
    if (this.modal) this.modal.destroy();
  }

  renderImagePreviewModal() {
    let images = this.state.previewImages;

    if (images) {
      let image = images[this.state.previewImageIndex];
      return (
        <Modal
          className="route-base-image-preview-modal"
          key="route-base-image-preview-modal"
          title={false}
          footer={null}
          open={true}
          closable={false}
          onCancel={this.onImagePreviewModalCancel}
        >
          {this.state.isImg ? (
            <img alt="" src={image?.url ?? image?.thumbUrl} />
          ) : (
            <video controls src={image?.url ?? image?.thumbUrl} />
          )}
        </Modal>
      );
    }

    return null;
  }

  onImagePreviewModalCancel = () => {
    this.setState({ previewImages: null, previewImageIndex: 0 });
  };

  /* router methods */

  goToLogin() {
    if (this.props && this.props.history) {
      this.props.history.replace({
        pathname: "/login",
        state: {
          from: this.props.location,
        },
      });
    }
  }

  setRouterQuery(queries = {}) {
    let { state, pathname } = this.props.location;
    this.props.history.replace(pathname, { ...state, ...queries });
  }

  getFromRouterQuery(key) {
    let { state } = this.props.location;
    return state ? state[key] : null;
  }

  getFromRouterState(key) {
    let state = this.props.location.state;
    return state ? state[key] : null;
  }

  static QueryValueType = {
    String: "string",
    Integer: "integer",
    Boolean: "boolean",
  };

  getRouterQueryValueByKey(key = "", valueType = Base.QueryValueType.String) {
    let value = this.getFromRouterQuery(key);
    if (value) {
      switch (valueType) {
        case Base.QueryValueType.Integer: {
          value = parseInt(value, 10);
          break;
        }
        case Base.QueryValueType.Boolean: {
          value = value === "true";
          break;
        }
        case Base.QueryValueType.String:
        default: {
          break;
        }
      }
    }
    return value;
  }

  getRouterQueryItemByKey(arr = [], key = "", defaultValue = 0) {
    let item = this.getFromRouterQuery(key);
    if (item) {
      item = arr.find((itm) => itm.key === item);
      if (!item) item = arr[defaultValue];
    } else {
      item = arr[defaultValue];
    }
    return item ? item : {};
  }

  getRouterQueryTimeRange() {
    let rangeTime = null;
    let queryTime = this.getFromRouterQuery("timeRange");
    if (queryTime) {
      let timeArr = queryTime.split("~");
      if (timeArr[1]) {
        let startTime = this.moment(timeArr[0]);
        let endTime = this.moment(timeArr[1]);
        if (
          startTime.isValid() &&
          endTime.isValid() &&
          startTime.diff(endTime) <= 0
        ) {
          rangeTime = [startTime, endTime];
        }
      }
    }
    return rangeTime;
  }

  getRouterQueryPage() {
    let page = this.getFromRouterQuery("page");
    if (page && !isNaN(parseInt(page, 10))) {
      page = parseInt(page, 10);
    } else {
      page = 1;
    }
    return page;
  }

  getRouterQueryKeyword() {
    let keyword = this.getFromRouterQuery("keyword");
    if (!keyword) {
      return "";
    }
    return keyword;
  }

  getRouterQuerySearchType() {
    let type = this.getFromRouterQuery("searchType");
    if (!type) {
      return "name";
    }
    return type;
  }

  getRouterQueryStartPrice() {
    const price = this.getFromRouterQuery("price_start");
    if (!price) {
      return "";
    }
    return price;
  }

  getRouterQueryEndPrice() {
    const price = this.getFromRouterQuery("price_end");
    if (!price) {
      return "";
    }
    return price;
  }

  getRouterQueryPageSize(defaultValue = this.isJdShop() ? 20 : 10) {
    let pageSize = this.getFromRouterQuery("pageSize");
    if (pageSize && !isNaN(parseInt(pageSize, 10))) {
      pageSize = parseInt(pageSize, 10);
    } else {
      pageSize = defaultValue;
    }
    return pageSize;
  }

  isRouterLbsRelated() {
    const routes = ["/main/delivery"];
    let pathname = this.props.location.pathname;
    return routes.some((item) => pathname.startsWith(item));
  }

  isRouterMerchantCommunityRelated() {
    return this.props.location.pathname.indexOf("/main/delivery/community") !== -1;
  }

  isRouterProxyRelated() {
    const routes = ["/main/proxy", "/main/balance"];
    let pathname = this.props.location.pathname;
    return routes.some((item) => pathname.startsWith(item));
  }

  isRouterWeappRelated() {
    const routes = [
      "/main/mini-program",
      "/main/users/list",
      "/main/shows",
      "/main/manage-ads/splash",
      "/main/manage-ads/home-ad",
      "/main/manage-ads/home-product-groups",
      "/main/live",
    ];
    let pathname = this.props.location.pathname;
    return routes.some((item) => pathname.startsWith(item));
  }

  isRouterStoreMembershipRelated() {
    const pathname = this.props.location.pathname || "";
    return (
      pathname.startsWith("/main/users/store-memberships") ||
      pathname.startsWith("/main/users/store-memberships-attribution")
    );
  }

  isRouterALRelated() {
    const routes = ["/main/article"];
    let pathname = this.props.location.pathname;
    return routes.some((item) => pathname.startsWith(item));
  }

  isRouterLotteryRelated() {
    const routes = ["/main/lottery/list"];
    let pathname = this.props.location.pathname;
    return routes.some((item) => pathname.startsWith(item));
  }

  isRouterLotteryQrcodeRelated() {
    const routes = ["/main/lottery/qrcode"];
    let pathname = this.props.location.pathname;
    return routes.some((item) => pathname.startsWith(item));
  }

  isRouterMembersRelated() {
    const pathname = this.props.location.pathname || "";
    // Use path prefix — "store-memberships" also contains the substring "members".
    return pathname.startsWith("/main/members");
  }

  isRouterAdminRelated() {
    const pathname = this.props.location.pathname;
    return pathname.startsWith("/main/admin");
  }

  isRouteWarehouseRelated(pathname = null) {
    const routes = [
      "/main/order",
      "/main/product",
      "/main/review",
      "/main/setting",
    ];

    if (!pathname) {
      pathname = this.props.location.pathname;
    }

    return routes.some((item) => pathname.startsWith(item));
  }

  /* redux methods */

  getUser() {
    return this.props.user;
  }

  getUserToken() {
    let user = this.getUser();
    return user ? user.token : null;
  }

  getUserRefreshToken() {
    let user = this.getUser();
    return user ? user.refresh_token : null;
  }

  getMerchantBusinessStatus() {
    let user = this.getUser();
    let merchant = user ? user.merchant : null;
    return merchant ? merchant.status : 0;
  }

  isMerchantInBusinuess() {
    return this.getMerchantBusinessStatus() === 1;
  }
  merchantHasCommission() {
    // Balance-topup VIP can coexist with per-product commission.
    // VIP package referral uses agent-level / balance-vip commission;
    // regular products still use the product commission tab.
    if (
      this.isBBShopMerchant() ||
      this.isAdminWithDabazaShop() ||
      (this.isThirdPartyShopHasWeapp() && this.shopHasAgents())
    ) {
      return true;
    }
    return false;
  }

  isBBShopMerchant(merchant = null) {
    let user = this.getUser();
    merchant = user ? user.merchant : null;
    if (merchant) {
      return [SUPERMARKET_ID, JD_ID, GROUPON_ID].includes(merchant.id);
    }
    return false;
  }

  isArticleEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_article;
  }
  isMultiUnitEnabled() {
    const { settings } = this.props;
    return settings && settings.multi_unit_enabled;
  }
  isWholesaleStoreVerificationEnabled() {
    const { settings } = this.props;
    return !!(settings && settings.wholesale_store_verification);
  }
  isProductEnabled() {
    const { settings } = this.props;
    return settings && !settings.is_product_disabled;
  }
  isPartnerEnabled() {
    const { settings } = this.props;
    return settings && settings.partner_enabled;
  }
  isShopOwnerEnabled() {
    const { settings } = this.props;

    return settings && settings.shop_owner_enabled;
  }
  isAgentLevelProductEnabled() {
    const { settings } = this.props;

    return settings && settings.agent_level_product_enabled;
  }

  isLotteryEnabled() {
    if (this.isLotteryQrcodeEnabled()) {
      return true;
    }
    const user = this.getUser();
    return user && user.merchant && user.merchant.enabled_lottery;
  }

  isLotteryQrcodeEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_lottery_qrcode;
  }

  isMembersEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_membership;
  }

  isBalanceVipEnabled() {
    const { settings } = this.props;
    return settings && settings.balance_topup_vip_enabled;
  }

  isNewWechatTransferEnabled() {
    const { settings } = this.props;
    return !!(settings && settings.enabled_transfer_new);
  }

  isSpecialShareEnabled() {
    const { settings } = this.props;
    return !!(settings && settings.special_share_enabled);
  }

  isTaskCenterEnabled() {
    const { settings } = this.props;
    return !!(settings && settings.enable_task_center);
  }

  isCourseEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_course;
  }

  isDabazaShop() {
    const user = this.getUser();
    return (
      (user.merchant && user.merchant.is_dabaza_shop) ||
      user.merchant.type === 14
    );
  }
  isBdShop() {
    const user = this.getUser();
    return user.merchant.type === 18;
  }
  isGrouponMerchant() {
    const user = this.getUser();
    return user && user.merchant && user.merchant.type === 19;
  }
  isShopsEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_shops;
  }
  isSoleLotteryWeapp() {
    const user = this.getUser();
    return (
      user &&
      user.merchant &&
      user.merchant.enabled_lottery &&
      user.merchant.type === 15
    );
  }
  isShopsEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_shops;
  }
  isVideoEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_video;
  }

  isCustomPageEnabled() {
    const { settings } = this.props;
    return settings && settings.enable_custom_page;
  }

  isEnabledSpecifiedUsers() {
    const user = this.getUser();
    return user.merchant && user.merchant.enabled_specified_user;
  }

  isThirdPartyShop() {
    const user = this.getUser();
    return [12, 13, 15, 17].includes(user.merchant.type);
  }

  isThirdPartyShopHasWeapp() {
    const user = this.getUser();
    return this.isThirdPartyShop() && user.merchant.has_weapp;
  }

  isLbsShop() {
    const user = this.getUser();
    return (
      user?.merchant &&
      user.merchant.is_lbs &&
      !user.merchant.is_bulutbazar_lbs_shop
    );
  }

  isSaasLbsShop() {
    const { settings } = this.props;
    if (settings && settings.is_saas_lbs_shop) {
      return true;
    }
    const user = this.getUser();
    return !!(user?.merchant && user.merchant.is_saas_lbs_shop);
  }

  isSaasShop() {
    const { settings } = this.props;
    if (settings && (settings.is_saas_shop || settings.is_saas_lbs_shop)) {
      return true;
    }
    const user = this.getUser();
    return !!(user?.merchant && user.merchant.is_saas_shop);
  }

  isMerchantCuratedPoiMode() {
    const { settings } = this.props;
    if (!settings || !settings.lbs_address_mode) {
      return false;
    }
    return (
      settings.lbs_address_mode === "merchant_poi" ||
      settings.lbs_address_mode === "merchant_poi_buildings"
    );
  }

  isMerchantPoiBuildingsMode() {
    const { settings } = this.props;
    return settings && settings.lbs_address_mode === "merchant_poi_buildings";
  }
  isBBLbsShop() {
    const user = this.getUser();
    return user?.merchant && user.merchant.is_bulutbazar_lbs_shop;
  }
  shopHasAgents() {
    const { settings } = this.props;
    if (settings && settings.disable_agent) {
      return false;
    }

    const user = this.getUser();
    return user?.merchant && user.merchant.has_agents;
  }

  showExtraContent() {
    const user = this.getUser();
    return user?.admin && this.isThirdPartyShop();
  }

  isAdminWithDabazaShop() {
    const user = this.getUser();
    return user?.admin && this.isDabazaShop();
  }

  /** Super admin: only these users can access the admin section (e.g. order debug). */
  isSuperAdmin() {
    const user = this.getUser();
    return !!user?.admin;
  }

  isJdShop() {
    const user = this.getUser();
    return user && user.merchant.id === JD_ID;
  }

  /* api methods */

  handleApiError(api, err) {
    if (!api || !err) return;

    this.logger.error(api.name, api, err);

    switch (err.status) {
      case 401: {
        this.onApi401Error(api, err);
        break;
      }
      case 408: {
        this.onApi408Error(api, err);
        break;
      }
      case 422: {
        this.onApi422Error(api, err);
        break;
      }
      case 429: {
        this.onApi429Error(api, err);
        break;
      }
      case 500: {
        this.onApi500Error(api, err);
        break;
      }
      case 503: {
        this.onApi503Error(api, err);
        break;
      }
      default: {
        this.onApiUknownError(api, err);
        break;
      }
    }
  }

  onApi401Error(api, err) {
    if (!api || !err) return;
    this.showModal(this.i18n.t("base.api.err401"), Base.Modal.warning, () => {
      this.goToLogin();
    });
  }

  onApi408Error(api, err) {
    if (!api || !err) return;

    this.showModal(this.i18n.t("base.api.err408"), Base.Modal.error);
  }

  onApi422Error(api, err) {
    if (!api || !err) return;

    let errors = err.data ? err.data : null;
    if (errors && errors.data) {
      errors = errors.data;
    }
    if (!errors) errors = err.data ? err.data.errors : null;

    let keys = errors ? Object.keys(errors) : null;
    if (keys && keys.length > 0) {
      let arr = [];
      for (let i = 0; i < keys.length; i++) {
        let value = errors[`${keys[i]}`];
        arr.push(value.toString());
      }

      let modalContent = (
        <div>
          {arr.map((item, index) => {
            return <h4 key={`err-${index}`}>{`${index + 1}. ${item}`}</h4>;
          })}
        </div>
      );
      this.showModal(
        this.i18n.t("base.api.err422Title"),
        Base.Modal.warning,
        () => {},
        {
          content: modalContent,
        }
      );
    } else {
      this.showModal(this.i18n.t("base.api.err422Empty"), Base.Modal.warning);
    }
  }

  onApi429Error(api, err) {
    if (!api || !err) return;

    this.showModal(this.i18n.t("base.api.err429"));
  }

  onApi500Error(api, err) {
    if (!api || !err) return;

    this.showModal(this.i18n.t("base.api.err500"));
  }

  onApi503Error(api, err) {
    if (!api || !err) return;

    this.showModal(this.i18n.t("base.api.err503"));
  }

  onApiUknownError(api, err) {
    console.log("🚀 ~ Base ~ onApiUknownError ~ api:", api, err);
    if (!api || !err) return;

    this.showMessage(this.i18n.t("base.api.errUnknown"), Base.Message.error);
  }
}

export default Base;
